// -----------------------------------------------------------------------------
// React Query hooks for the sector-template catalog (Phase 8 Wave 8.E).
// -----------------------------------------------------------------------------

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { DocumentTypeResponse } from '../../schema/api';
import { SchemaQueryKeys } from '../../schema/api';
import {
  getSectorTemplate,
  instantiateSectorTemplate,
  listSectorTemplates,
  SectorTemplateQueryKeys,
  type InstantiateSectorTemplateBody,
  type SectorName,
  type SectorTemplateDetailResponse,
  type SectorTemplateSummaryResponse,
} from './api';

/** Lists the catalog. Optional `sector` filter and `q` free-text search pass through to the API. */
export function useSectorTemplates(
  sector?: SectorName,
  q?: string,
): UseQueryResult<SectorTemplateSummaryResponse[]> {
  return useQuery({
    queryKey: SectorTemplateQueryKeys.list(sector, q),
    queryFn: ({ signal }) => listSectorTemplates(sector, q, signal),
    // Catalog content rarely changes within a session — cache for 5 min.
    staleTime: 5 * 60_000,
  });
}

export function useSectorTemplate(
  id: string | undefined,
): UseQueryResult<SectorTemplateDetailResponse> {
  return useQuery({
    queryKey: id !== undefined ? SectorTemplateQueryKeys.detail(id) : ['sector-templates', 'detail', '__skip__'],
    queryFn: ({ signal }) => getSectorTemplate(id as string, signal),
    enabled: id !== undefined,
    staleTime: 5 * 60_000,
  });
}

export interface InstantiateVariables {
  templateId: string;
  body: InstantiateSectorTemplateBody;
}

export function useInstantiateSectorTemplate(): UseMutationResult<
  DocumentTypeResponse,
  Error,
  InstantiateVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, body }) => instantiateSectorTemplate(templateId, body),
    onSuccess: (created) => {
      // The new draft joins the document-types list — refresh both the list and the slug detail
      // so the editor lands on data that is already in cache.
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.list() });
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.detail(created.slug) });
    },
  });
}
